import { ExternalLink } from "lucide-react";
import type { StockOut, InventoryItem } from "@workspace/api-client-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

// Read-only counterpart of MarkStockOutPaidDialog — shows the payment
// receipt for an already-Paid cart: mode, transaction id, the line
// items inside the cart, and the merchant-copy file (inline preview
// for images, embedded iframe for PDFs / other).

interface CartTarget {
  id: string;
  cartId: string | null;
  rows: StockOut[];
  totalAmount: number;
}

interface Props {
  cart: CartTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemById: Map<number, InventoryItem>;
}

export function ViewStockOutReceiptDialog({ cart, open, onOpenChange, itemById }: Props) {
  // All rows in a cart share the same payment fields — read from any.
  const first = cart?.rows[0] ?? null;
  const proofUrl = first?.paymentProofUrl ?? null;
  const isImage = !!proofUrl && /\.(png|jpe?g|webp|gif)(\?|$)/i.test(proofUrl);
  const heading =
    cart && cart.rows.length === 1
      ? itemById.get(cart.rows[0]!.itemId)?.name ?? `Item #${cart.rows[0]!.itemId}`
      : cart
      ? `Cart of ${cart.rows.length} items`
      : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto flex flex-col"
      >
        <SheetHeader>
          <SheetTitle>Payment Receipt</SheetTitle>
          <SheetDescription>
            {heading ? `Receipt for ${heading}.` : "Recorded payment details."}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 text-sm mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Payment Mode</p>
              <p className="font-medium">{first?.paymentMethod ?? "—"}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Transaction ID</p>
              <p className="font-mono break-all">{first?.invoiceReference ?? "—"}</p>
            </div>
          </div>

          {/* Cart breakdown — single-row carts just show one line.
              Always rendered so the operator can verify the merchant
              copy total against the per-item totals. */}
          {cart && (
            <div className="space-y-1 rounded-md border bg-muted/30 p-3">
              <p className="text-xs text-muted-foreground">
                Items {cart.rows.length > 1 ? `(${cart.rows.length})` : ""}
              </p>
              <ul className="space-y-0.5">
                {cart.rows.map((r) => {
                  const it = itemById.get(r.itemId);
                  return (
                    <li
                      key={`vr-line-${r.id}`}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="truncate">
                        {it?.name ?? `Item #${r.itemId}`}{" "}
                        <span className="text-xs text-muted-foreground">
                          × {Number(r.quantityIssued)}
                        </span>
                      </span>
                      <span className="font-mono tabular-nums shrink-0">
                        ₹{Number(r.amountCharged ?? 0).toLocaleString("en-IN")}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div className="pt-2 mt-1 border-t flex items-center justify-between font-medium">
                <span>Total</span>
                <span className="font-mono tabular-nums">
                  ₹{cart.totalAmount.toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Payment Proof (Merchant Copy)
              </p>
              {proofUrl && (
                <a
                  href={proofUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  Open in new tab
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            {proofUrl ? (
              isImage ? (
                <a
                  href={proofUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-md border overflow-hidden hover:opacity-90"
                >
                  <img
                    src={proofUrl}
                    alt="Payment proof"
                    className="w-full object-contain bg-muted/30 max-h-[70vh]"
                  />
                </a>
              ) : (
                <iframe
                  title="Payment proof"
                  src={`${proofUrl}#toolbar=0&view=FitH`}
                  className="w-full h-[70vh] rounded-md border bg-muted/30"
                />
              )
            ) : (
              <p className="text-muted-foreground rounded-md border bg-muted/30 p-4 text-center">
                No proof attached.
              </p>
            )}
          </div>
        </div>

        <div className="mt-auto pt-4 flex justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
